import type { FC } from 'react';
import React, { useEffect, useState, useRef, createRef } from 'react';
import type { TChangeEventHandler, TFocusEventHandler } from '@alfa-bank/site-input/models';

import {
    getHighlightedIndex,
    onInputKeyDown,
    onDropdownSelect,
    onBlurInput,
    callIfBlurred,
} from './helpers';
import type { TSelect } from './models';

const noop = /* istanbul ignore next */ () => {};

export const Select: FC<React.PropsWithChildren<TSelect>> = ({
    allowToShowEmptyListMessage,
    dataTestId = 'input',
    ArrowIcon,
    Autocomplete,
    Input,
    addHiddenInput = false,
    arrow = true,
    autoComplete = 'off',
    caption = '',
    children,
    disabled = false,
    error = false,
    forwardedRef,
    name: selectName = '',
    onCancel,
    onChange,
    onEmptyListMessageClick,
    options = [],
    placeholder = '',
    onClick,
    readOnly = false,
    styles,
    success = false,
    value: selectValue = '',
    type = 'text',
    onBlur = noop,
    onFocus = noop,
    onSelect = noop,
}) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = forwardedRef || createRef();
    const autocompleteRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState(selectValue);
    const [highlightedItem, setHighlightedItem] = useState(
        getHighlightedIndex(inputValue, options)
    );
    const onInputFocus: TFocusEventHandler = rest => {
        setIsOpen(true);
        onFocus(rest);
    };
    const onClickChange = () => setIsOpen(true);
    const onDropdownToggle = (event: React.MouseEvent<SVGSVGElement>) => {
        onClick?.({ event });
        setIsOpen(!isOpen);
    };
    const onInputChange: TChangeEventHandler = event => {
        onChange?.(event);

        if (!isOpen) {
            setIsOpen(true);
        }
    };

    useEffect(() => {
        setHighlightedItem(getHighlightedIndex(selectValue, options));
    }, [selectValue, options]);

    return (
        <div ref={wrapperRef} className={styles.wrapper}>
            <Input
                addHiddenInput={addHiddenInput}
                autoComplete={autoComplete}
                caption={caption}
                dataTestId={dataTestId}
                disabled={disabled}
                error={error}
                forwardedRef={inputRef}
                hideStatusIcon={!onChange}
                name={selectName}
                onBlur={onBlurInput({
                    wrapperRef,
                    setIsOpen,
                    onBlur,
                })}
                onCancel={onCancel}
                onChange={onInputChange}
                onClick={onClickChange}
                onFocus={onInputFocus}
                onKeyDown={onInputKeyDown({
                    name: selectName,
                    options,
                    highlightedItem,
                    setInputValue,
                    setIsOpen,
                    setHighlightedItem,
                    onSelect,
                })}
                placeholder={placeholder}
                readOnly={readOnly}
                success={success}
                type={type}
                value={selectValue}
            />

            {arrow && !onChange && (
                <ArrowIcon
                    isOpen={isOpen && !disabled}
                    onBlur={() => {
                        callIfBlurred(wrapperRef, () => {
                            setIsOpen(false);
                        });
                    }}
                    onClick={onDropdownToggle}
                    tabIndex={-1}
                />
            )}

            {isOpen && !disabled && (
                <Autocomplete
                    allowToShowEmptyListMessage={allowToShowEmptyListMessage}
                    autocompleteRef={autocompleteRef}
                    highlightedItem={highlightedItem}
                    onEmptyListMessageClick={onEmptyListMessageClick}
                    onSelect={onDropdownSelect({
                        name: selectName,
                        options,
                        onSelect,
                        setInputValue,
                        setIsOpen,
                        setHighlightedItem,
                    })}
                    options={options}
                >
                    {children}
                </Autocomplete>
            )}
        </div>
    );
};
